import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { NotePencil, PencilSimple, Trash, CheckCircle, LinkSimple, ChatCircle, Eraser, Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { useYouTubePlayer } from "@/hooks/useYouTubePlayer";
import { DrawingCanvas, ColorPicker } from "@/components/DrawingCanvas";
import { useSeo } from "@/lib/seo";

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (id) => `${BACKEND_URL}/api/files/${id}/content`;

const NoteCard = ({ note, onSeek, onResolve, onDelete, onReply, isOwner }) => {
  const [replyText, setReplyText] = useState("");
  const sendReply = async () => {
    if (!replyText.trim()) return;
    await onReply(note.id, replyText);
    setReplyText("");
  };
  return (
    <div className={`bg-[var(--bg)] border rounded-[14px] p-3.5 group ${note.resolved ? "border-[var(--success)]/40 opacity-70" : "border-[var(--border-c)]"}`} data-testid={`note-card-${note.id}`}>
      <div className="flex items-center justify-between gap-2">
        <button data-testid={`note-seek-${note.id}`} onClick={() => onSeek(note)} className="text-[10px] font-bold text-[var(--glow)] bg-[var(--glow)]/10 rounded-full px-2.5 py-1 hover:bg-[var(--glow)]/20 transition-colors flex items-center gap-1">
          {fmtTime(note.timeSeconds)}{note.drawing?.length > 0 && <PencilSimple size={10} weight="bold" />}
        </button>
        <div className="flex items-center gap-2">
          <button data-testid={`note-resolve-${note.id}`} onClick={() => onResolve(note)} title={note.resolved ? "Reopen" : "Mark resolved"}>
            <CheckCircle size={16} weight={note.resolved ? "fill" : "regular"} color={note.resolved ? "var(--success)" : "var(--muted)"} />
          </button>
          {isOwner && (
            <button data-testid={`note-delete-${note.id}`} onClick={() => onDelete(note.id)} className="opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--coral)] transition-opacity">
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm mt-2">{note.text}</p>
      <p className="text-[10px] text-[var(--muted)] mt-1">{note.author} · {dayjs(note.createdAt).format("MMM D, HH:mm")}</p>
      {note.replies?.map((r) => (
        <div key={r.id} className="mt-2 ml-3 pl-3 border-l-2 border-[var(--border-c)]">
          <p className="text-xs">{r.text}</p>
          <p className="text-[10px] text-[var(--muted)] mt-0.5">{r.author} · {dayjs(r.createdAt).format("MMM D, HH:mm")}</p>
        </div>
      ))}
      <div className="flex gap-2 mt-2.5">
        <input
          data-testid={`note-reply-input-${note.id}`}
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendReply()}
          placeholder="Reply..."
          className="flex-1 bg-[var(--card)] border border-[var(--border-c)] rounded-full px-3 py-1.5 text-xs outline-none focus:border-[var(--glow)]"
        />
        <button data-testid={`note-reply-btn-${note.id}`} onClick={sendReply} className="text-[var(--glow)]"><ChatCircle size={16} /></button>
      </div>
    </div>
  );
};

export default function ReviewWorkspace({ shared = false }) {
  const { id, token } = useParams();
  const ref = shared ? token : id;
  useSeo("Review Workspace", "Timestamped notes and on-frame drawings for video QC.");
  const [review, setReview] = useState(null);
  const [drawMode, setDrawMode] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [color, setColor] = useState("#FF4D4D");
  const [noteText, setNoteText] = useState("");
  const [guestName, setGuestName] = useState(() => localStorage.getItem("review_guest_name") || "");
  const [frameFileId, setFrameFileId] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const isFile = review?.sourceType === "file";
  const yt = useYouTubePlayer(isFile ? null : review?.videoId);
  const videoRef = { current: null };
  const setVideoRef = (el) => { videoRef.current = el; window.__reviewVideo = el; };
  const pause = () => (isFile ? window.__reviewVideo?.pause() : yt.pause());
  const seekTo = (t) => {
    if (isFile && window.__reviewVideo) {
      window.__reviewVideo.currentTime = t;
      window.__reviewVideo.pause();
    } else yt.seekTo(t);
  };
  const getTime = () => (isFile ? window.__reviewVideo?.currentTime || 0 : yt.getTime());
  const containerRef = yt.containerRef;

  const load = useCallback(async () => {
    const res = await api.get(`/reviews/${ref}`);
    setReview(res.data);
  }, [ref]);

  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const startNote = () => {
    pause();
    if (!drawMode) setStrokes([]);
  };

  const captureFrame = async () => {
    const v = window.__reviewVideo;
    if (!v || !v.videoWidth) return toast.error("Play the video first, then capture");
    v.pause();
    setCapturing(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0);
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.82));
      const form = new FormData();
      form.append("file", new File([blob], `frame-${Math.floor(getTime())}s.jpg`, { type: "image/jpeg" }));
      const res = await api.post("/upload", form);
      setFrameFileId(res.data.id);
      toast.success("Frame captured — it will attach to your note");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCapturing(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim() && strokes.length === 0) return toast.error("Write a note or draw on the frame first");
    try {
      await api.post(`/reviews/${ref}/notes`, {
        timeSeconds: getTime(),
        text: noteText,
        drawing: strokes.length ? strokes : null,
        frameFileId,
        author: guestName || undefined,
      });
      if (guestName) localStorage.setItem("review_guest_name", guestName);
      setNoteText("");
      setStrokes([]);
      setFrameFileId(null);
      setDrawMode(false);
      toast.success("Note added");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const onSeek = (note) => {
    seekTo(note.timeSeconds);
    setStrokes(note.drawing || []);
    setDrawMode(false);
  };

  const onResolve = async (note) => {
    await api.put(`/reviews/${ref}/notes/${note.id}`, { resolved: !note.resolved });
    load();
  };

  const onDelete = async (nid) => {
    await api.delete(`/reviews/${ref}/notes/${nid}`);
    load();
  };

  const onReply = async (nid, text) => {
    await api.post(`/reviews/${ref}/notes/${nid}/replies`, { text, author: guestName || undefined });
    if (guestName) localStorage.setItem("review_guest_name", guestName);
    load();
  };

  const copyShare = () => {
    navigator.clipboard.writeText(`${window.location.origin}/review/share/${review.shareToken}`);
    toast.success("Share link copied — anyone with it can view and reply");
  };

  if (!review) return <div className="p-16 text-center text-[var(--muted)] text-sm">Loading review...</div>;

  const open = review.notes.filter((n) => !n.resolved).length;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12" data-testid="review-workspace">
      <div className="fade-up mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-black tracking-tight truncate">{review.title}</h1>
          <p className="text-xs text-[var(--muted)] mt-1" data-testid="notes-summary">{review.notes.length} notes · {open} open</p>
        </div>
        {review.isOwner && (
          <button data-testid="copy-share-link-btn" onClick={copyShare} className="inline-flex items-center gap-2 px-4 py-2 rounded-full surface text-xs font-semibold hover:border-[var(--glow)] transition-colors">
            <LinkSimple size={14} weight="bold" /> Copy share link
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div>
          <div className="relative rounded-[14px] overflow-hidden bg-black aspect-video">
            {isFile ? (
              <video
                ref={setVideoRef}
                data-testid="html5-player"
                src={fileUrl(review.fileId)}
                controls
                className="absolute inset-0 w-full h-full"
                crossOrigin="anonymous"
              />
            ) : (
              <div ref={containerRef} className="absolute inset-0 w-full h-full [&>iframe]:w-full [&>iframe]:h-full" />
            )}
            <DrawingCanvas active={drawMode} strokes={strokes} onStrokesChange={setStrokes} color={color} />
          </div>
          <div className="surface rounded-[14px] p-4 mt-3 flex flex-wrap items-center gap-3">
            <button
              data-testid="draw-mode-btn"
              onClick={() => { setDrawMode(!drawMode); if (!drawMode) startNote(); }}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${drawMode ? "bg-[var(--coral)] text-white" : "surface hover:border-[var(--glow)]"}`}
            >
              <PencilSimple size={14} weight="bold" /> {drawMode ? "Drawing on frame" : "Draw on frame"}
            </button>
            {isFile && (
              <button
                data-testid="capture-frame-btn"
                onClick={captureFrame}
                disabled={capturing}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-colors ${frameFileId ? "bg-[var(--success)]/15 border border-[var(--success)]/40 text-[var(--success)]" : "surface hover:border-[var(--glow)]"} disabled:opacity-60`}
              >
                <Camera size={14} weight="bold" /> {capturing ? "Capturing..." : frameFileId ? "Frame attached ✓" : "Capture frame"}
              </button>
            )}
            {drawMode && <ColorPicker color={color} onChange={setColor} />}
            {strokes.length > 0 && (
              <button data-testid="clear-drawing-btn" onClick={() => setStrokes([])} className="inline-flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--coral)] transition-colors">
                <Eraser size={14} /> Clear
              </button>
            )}
            <p className="text-[10px] text-[var(--muted)] ml-auto">Pause the video, draw, then add your note — it saves the drawing at that timestamp.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="surface rounded-[20px] p-5" data-testid="add-note-panel">
            <p className="text-sm font-semibold flex items-center gap-2 mb-3"><NotePencil size={16} color="var(--glow)" weight="duotone" /> New note at current time</p>
            {!review.isOwner && (
              <input
                data-testid="guest-name-input"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Your name"
                className="w-full mb-2.5 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-3.5 py-2 text-sm outline-none focus:border-[var(--glow)]"
              />
            )}
            <textarea
              data-testid="note-text-input"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onFocus={startNote}
              rows={3}
              placeholder="e.g. Color grade too warm here — pull back the orange"
              className="w-full bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-3.5 py-2.5 text-sm outline-none focus:border-[var(--glow)] resize-y"
            />
            <button data-testid="add-note-btn" onClick={addNote} className="mt-3 w-full py-2.5 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors">
              Add note{strokes.length > 0 && " + drawing"}{frameFileId && " + frame"}
            </button>
          </div>

          <div className="space-y-2.5 max-h-[520px] overflow-y-auto pr-1" data-testid="notes-list">
            {review.notes.length === 0 && <p className="text-[var(--muted)] text-sm text-center py-6">No notes yet. Pause and leave the first one.</p>}
            {review.notes.map((n) => (
              <NoteCard key={n.id} note={n} onSeek={onSeek} onResolve={onResolve} onDelete={onDelete} onReply={onReply} isOwner={review.isOwner} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
